import { useCallback } from 'react';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { toastApiRef } from '@backstage/frontend-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { useByxT } from '../shared/translations';

/**
 * `catalog.entity.refresh`, the permission the stock About card checks
 * before offering a refresh (`catalogEntityRefreshPermission` in
 * `@backstage/plugin-catalog-common`, which the app does not depend on).
 * The catalog backend enforces it again on the request itself; a contract
 * test keeps this copy equal to the installed definition.
 */
export const REFRESH_PERMISSION: Parameters<typeof useEntityPermission>[0] = {
  type: 'resource',
  name: 'catalog.entity.refresh',
  attributes: { action: 'update' },
  resourceType: 'catalog-entity',
};

/** Only entities read from a url/file location can be refreshed. */
export function canScheduleRefresh(entity: Entity): boolean {
  const location =
    entity.metadata.annotations?.['backstage.io/managed-by-location'];
  return !!location && /^(url|file):/.test(location);
}

/** Toasts close on their own, as the transient alerts they replace did. */
const TOAST_TIMEOUT_MS = 5000;

/**
 * "Atualizar entidade" in the ⋯ menu: the refresh the stock About card
 * offered, which the v2 page replaces. Same call, same permission.
 */
export function useRefreshMenuItemProps() {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const toastApi = useApi(toastApiRef);
  const { t } = useByxT();
  const { allowed } = useEntityPermission(REFRESH_PERMISSION);
  const onClick = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      toastApi.post({
        title: t('entity.menu.refreshScheduled'),
        status: 'info',
        timeout: TOAST_TIMEOUT_MS,
      });
    } catch {
      // A generic message, never the raw API error (SR-12).
      toastApi.post({
        title: t('entity.menu.refreshFailed'),
        status: 'danger',
        timeout: TOAST_TIMEOUT_MS,
      });
    }
  }, [catalogApi, toastApi, entity, t]);
  return {
    title: t('entity.menu.refresh'),
    disabled: !allowed,
    onClick,
  };
}

export const refreshMenuIcon = <RefreshIcon fontSize="small" />;
